/**
 * `AW3` · Las tres acciones de la fila: Eliminar, Ver motivo y Reintentar.
 *
 * `F1.md` §P0-2 y §P0-3, y §Pantalla: «Acciones: Eliminar · (si estado = error)
 * Ver motivo y Reintentar».
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LAS TRES DECISIONES QUE ESTE ARCHIVO LLEVA CON MARCADOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ① **Eliminar pide una confirmación EXPLÍCITA y recién entonces borra. No hay
 *   deshacer.** El criterio dice «hace clic en Eliminar **y confirma**», y `D11`
 *   —riesgo aceptado por escrito— establece que no hay versionado ni historial.
 *   La implementación equivocada es el patrón de UI más común para esto: borrado
 *   optimista con un «deshacer» de cinco segundos. Promete una vuelta atrás que
 *   la base no puede dar, porque del otro lado la cascada por foreign key de `K1`
 *   ya borró fragmentos y embeddings. **El «deshacer» que falla es peor que no
 *   tenerlo: el operador ya soltó la atención cuando se entera.** Por eso acá la
 *   fila **no desaparece** hasta que el borde contestó.
 *
 * ② **«Ver motivo» muestra el motivo LEGIBLE que trae el borde, y NO le agrega
 *   el error del proveedor.** El motivo ya viene en lenguaje legible por decisión
 *   de `K5`. Quien lee esta pantalla es un operador de Elige Educar, no quien
 *   escribió el worker: pegarle abajo el `502` de un proveedor o un stack cumple
 *   la letra del criterio y no el criterio. El detalle técnico se guarda del otro
 *   lado, donde hace falta para diagnosticar.
 *
 *   ⟲ RASTRO · 25/9/2026 · `AW60` · ADR-0384 A Decisión 2 (`R1-B-007`). El
 *   diálogo muestra además la ETAPA, la CAUSA clasificada y el DETALLE DEL
 *   PROVEEDOR que `DocumentListItem` trae desde `CR6`. El criterio de arriba
 *   SIGUE: los tres son campos que el contrato ya fija constantes —la etapa y la
 *   causa son listas cerradas, traducidas acá por tabla cerrada; el detalle es la
 *   frase de `PROVIDER_DETAIL_BY_CAUSE` más, como mucho, un estado del proveedor
 *   que pasó `PROVIDER_STATUS_SHAPE`—. Nada de esto es el mensaje de una
 *   excepción ni el nombre del archivo, y la pantalla no compone texto propio
 *   con ellos: los muestra, rotulados, uno por renglón.
 *
 * ③ **«Reintentar» se ofrece únicamente sobre un documento en `error`, y la
 *   pantalla NO limpia nada antes de llamar.** La limpieza de fragmentos
 *   parciales es de `K5`, vive en `pipeline/retry.ts` atada al re-disparo, y
 *   desde acá ni siquiera es alcanzable: la pantalla no tiene base. El intento
 *   tomaría la forma de un segundo llamado al borde antes del reintento, que
 *   correría sin transacción y sin ver el estado real del pipeline. La variante
 *   más chica del mismo error es ofrecer el botón en cualquier estado: reintentar
 *   un documento `indexado` lo saca de disponible para el chatbot sin que nadie
 *   lo haya pedido.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DE DÓNDE SALE CADA COSA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * De las tres acciones, **sólo dos son escritura**. «Ver motivo» es LECTURA:
 * `DocumentListItem.errorReason` ya viaja en cada fila de `contracts/read-api`
 * —su propio comentario dice «Habilita la acción “Ver motivo”»— y no toca
 * `contracts/admin-api` en absoluto. Que no haya un `verMotivo()` en el borde de
 * escritura no es una omisión: es que el dato ya está.
 */

import { useState } from "react"
import { useBordeDeEscritura } from "../contexto-borde-escritura.tsx"
import type { DocumentErrorCause, DocumentErrorStep } from "@contracts/knowledge/index.ts"
import type { FilaDocumento } from "../proveedor-datos.ts"
import { aLosBrazos, brazos, losBrazos, sellosDeLosBrazos } from "../zonas.ts"
import { Dialogo } from "./dialogo.tsx"

/**
 * El estado `error`, escrito UNA vez.
 *
 * Es el literal de `contracts/knowledge` tal como llega en `DocumentListItem`,
 * el mismo que la tabla muestra sin traducir. En una constante para que las dos
 * preguntas que dependen de él —¿se ofrece «Ver motivo»? ¿se ofrece
 * «Reintentar»?— no puedan responderse distinto por un typo en una de las dos.
 */
const ESTADO_ERROR: FilaDocumento["state"] = "error"

/** `guard` ③ · la única condición bajo la cual se ofrece reintentar. */
export function sePuedeReintentar(documento: FilaDocumento): boolean {
  return documento.state === ESTADO_ERROR
}

/** `guard` ② · la única condición bajo la cual se ofrece ver el motivo. */
export function seOfreceVerMotivo(documento: FilaDocumento): boolean {
  return documento.state === ESTADO_ERROR
}

/**
 * `AW60` · guard ① · LA ETAPA, traducida por tabla cerrada (`ver-motivo.md`,
 * acceptance «Rótulos»). Las claves son `DOCUMENT_ERROR_STEP_VALUES`: el tipo
 * `Record<DocumentErrorStep, …>` hace que `tsc` rechace una etapa de más o de
 * menos respecto del contrato.
 */
export const ETAPA_DEL_ERROR: Readonly<Record<DocumentErrorStep, string>> = {
  extraccion: "Extracción",
  fragmentacion: "Fragmentación",
  embeddings: "Embeddings",
  indexado: "Indexado",
}

/**
 * `AW60` · guard ① · LA CAUSA, con la frase del contrato y nunca con su
 * identificador: copia a mano de `DOCUMENT_ERROR_REASON_BY_CAUSE`
 * (`contracts/knowledge/index.ts`, `CK4`).
 *
 * ⛔ Copia y no import: la tabla es un VALOR de `contracts/`, y un import de
 * valor en `src/**` pasa `tsc` y rompe `vite build` (`ADR-0330`). Es el molde de
 * `ADR-0356` (`configuracion-del-chatbot/auditoria.tsx`): las CLAVES las ancla el
 * tipo `Record<DocumentErrorCause, …>` —`tsc` rechaza una causa de más o de
 * menos— y los TEXTOS los ancla `pruebas/aw60-ver-motivo.test.tsx`, que
 * sí puede importar el valor y afirma igualdad profunda contra esta copia.
 */
export const FRASE_DE_LA_CAUSA: Readonly<Record<DocumentErrorCause, string>> = {
  "proveedor-sin-respuesta":
    "No se pudo extraer el texto: el servicio de extracción no contestó dentro del tiempo de espera.",
  "proveedor-no-disponible":
    "No se pudo extraer el texto: el servicio de extracción estaba fuera de servicio cuando se le pidió.",
  "proveedor-saturado":
    "No se pudo extraer el texto: el servicio de extracción rechazó el pedido por exceso de pedidos simultáneos.",
  "archivo-vacio-o-corrupto": "No se obtuvo texto del archivo: está vacío, dañado o no contiene texto legible.",
  "proveedor-fallo-el-trabajo": "El servicio de extracción recibió el archivo pero terminó su trabajo con un fallo.",
  "falla-de-la-base":
    "El documento no se pudo guardar en el banco de contenido: falló la base de datos durante el procesamiento.",
  desconocida: "El procesamiento falló por una causa que el sistema no tiene clasificada.",
  "registro-no-escrito":
    "El procesamiento falló y además no se pudo guardar su registro técnico; el motivo de esta fila puede estar incompleto.",
}

/**
 * Los renglones de diagnóstico de «Ver motivo», en orden: etapa, causa, detalle.
 *
 * `errorStep` `null` (documentos anteriores a `K21`) ⇒ ninguno: el diálogo queda
 * como antes, sólo con el motivo. Un renglón cuyo valor no está en la tabla
 * cerrada (un valor que el contrato agregue antes que esta pantalla) NO se
 * dibuja: mostrar el identificador crudo es lo que el guard ① prohíbe. Sin
 * detalle del proveedor (`null`), ese renglón no se dibuja.
 */
export function renglonesDelMotivo(
  documento: FilaDocumento,
): readonly { readonly rotulo: string; readonly valor: string }[] {
  // `?? null`: una fila de un `admin-web` anterior a `CR6` no trae los campos.
  const paso = documento.errorStep ?? null
  if (paso === null) return []
  const renglones: { rotulo: string; valor: string }[] = []
  const etapa: string | undefined = ETAPA_DEL_ERROR[paso]
  if (etapa !== undefined) renglones.push({ rotulo: "Etapa", valor: etapa })
  const causaId = documento.errorCause ?? null
  const causa: string | undefined = causaId === null ? undefined : FRASE_DE_LA_CAUSA[causaId]
  if (causa !== undefined) renglones.push({ rotulo: "Causa", valor: causa })
  const detalle = documento.errorProviderDetail ?? null
  if (typeof detalle === "string" && detalle !== "") renglones.push({ rotulo: "Detalle del proveedor", valor: detalle })
  return renglones
}

type Abierto = "ninguno" | "confirmar-borrado" | "motivo"

export function AccionesDeFila({
  documento,
  alCambiarElBanco,
}: {
  readonly documento: FilaDocumento
  /** Refresca la tabla. Se llama DESPUÉS de que el borde contestó, nunca antes. */
  readonly alCambiarElBanco: () => void
}) {
  const borde = useBordeDeEscritura()
  const [abierto, setAbierto] = useState<Abierto>("ninguno")
  const [enCurso, setEnCurso] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * `guard` ① · el borrado, DESPUÉS de la confirmación.
   *
   * La secuencia entera es: abrir el diálogo → el operador aprieta «Sí, eliminar»
   * → **una** llamada a `remove()` → cuando contesta, cerrar y refrescar. No hay
   * ningún estado optimista en el medio: la fila sigue en la tabla mientras el
   * borde trabaja, y desaparece porque la lista se volvió a pedir y ya no está.
   * Sacarla antes sería prometer un borrado que todavía no ocurrió.
   */
  const eliminar = async () => {
    if (enCurso) return
    setEnCurso(true)
    setError(null)
    try {
      await borde.remove({ docId: documento.id })
      setAbierto("ninguno")
      alCambiarElBanco()
    } catch (causa) {
      setError(causa instanceof Error ? causa.message : String(causa))
    } finally {
      setEnCurso(false)
    }
  }

  /**
   * `guard` ③ · el reintento. **Una sola llamada, y nada antes.**
   *
   * No hay un `remove()` previo, ni un borrado de fragmentos, ni un cambio de
   * estado desde acá. Se llama `retry()` y se refresca la tabla para ver el
   * estado que el pipeline vaya dejando — que es de `contracts/read-api`, el
   * borde de lectura, y no de éste.
   */
  const reintentar = async () => {
    if (enCurso) return
    setEnCurso(true)
    setError(null)
    try {
      await borde.retry({ docId: documento.id })
      alCambiarElBanco()
    } catch (causa) {
      setError(causa instanceof Error ? causa.message : String(causa))
    } finally {
      setEnCurso(false)
    }
  }

  return (
    <div className="banco__acciones">
      <button
        type="button"
        className="boton boton--peligro"
        onClick={() => {
          setError(null)
          setAbierto("confirmar-borrado")
        }}
        disabled={enCurso}
        aria-label={`Eliminar ${documento.name}`}
      >
        Eliminar
      </button>

      {/*
        `F1.md` §Pantalla · «(si estado = error) Ver motivo y Reintentar».

        Las dos condiciones pasan por una función con nombre en vez de un
        `documento.state === "error"` repetido: el literal del estado queda en un
        solo lugar y las dos preguntas no pueden desincronizarse.
      */}
      {seOfreceVerMotivo(documento) && (
        <button
          type="button"
          className="boton"
          onClick={() => setAbierto("motivo")}
          disabled={enCurso}
          aria-label={`Ver motivo del error de ${documento.name}`}
        >
          Ver motivo
        </button>
      )}

      {sePuedeReintentar(documento) && (
        <button
          type="button"
          className="boton"
          onClick={() => {
            void reintentar()
          }}
          disabled={enCurso}
          aria-label={`Reintentar ${documento.name}`}
        >
          {enCurso ? "Reintentando…" : "Reintentar"}
        </button>
      )}

      {/*
        El error de una acción se muestra en la fila, no en un cartel global: el
        operador tiene que saber CUÁL documento falló, y con veinte filas un
        cartel arriba no lo dice.
      */}
      {error !== null && abierto === "ninguno" && (
        <span className="banco__error-accion" role="alert">
          {error}
        </span>
      )}

      {/* ── `guard` ① · LA CONFIRMACIÓN ───────────────────────────────────────── */}
      {abierto === "confirmar-borrado" && (
        <Dialogo titulo="Eliminar documento" onCerrar={() => setAbierto("ninguno")}>
          <p>
            Se va a eliminar <strong>{documento.name}</strong> del banco, junto con todos sus
            fragmentos y embeddings.
          </p>
          {/*
            ⭐ `F3-CA-29` (PRD:537) — EL POPUP DICE QUE AFECTA A LOS DOS BRAZOS, y el
            texto sale de `PRD_R1.1.md:495`, que es donde el popup está descrito
            entero: *«Afecta a los dos brazos: el chatbot de KAI · Brazo Híbrido y de
            KAI · Brazo Puro deja de usar esto desde este momento.»* El criterio sólo
            cita la primera mitad («dice «Afecta a los dos brazos»»); se escribe la
            frase completa del PRD y no el fragmento, porque `PRD:495` es la fuente
            del copy y el criterio es un recorte suyo.

            ⛔ Y va ACÁ, no en el aviso de la pantalla: el de `tabla.tsx`
            (`F3-CA-03`) dice a quién le pega un **cambio del banco**; éste dice a
            quién le pega **la eliminación de este documento**, y es la información
            con la que el operador decide. Es la mitad «…y dice que afecta a los dos
            brazos» de la regla 3 (`PRD_R1.1.md:501`): «Toda eliminación pide
            confirmación, nombra lo que elimina y dice que afecta a los dos brazos;
            sin confirmar no se elimina.»

            ⚠️ Sin clase propia: el párrafo hermano —el que nombra el documento—
            tampoco la lleva, y una clase nueva sin regla en `estilos.css` es la
            deuda que `tabla.tsx` ya reporta con `.nota-brazo`. No se estrena una
            segunda por el mismo motivo.
          */}
          <p>
            {/*
              ⟦rastro · ADR-0082 · ADR-0402 · 30/9/2026 · R3-H-005⟧ Acá decía ~~«el chatbot de
              KAI · Brazo Híbrido y de KAI · Brazo Puro»~~, con «KAI» literal. Mismo arreglo que
              el de `tabla.tsx`, con el mismo helper. Ningún caso de R3 mira esta línea.
            */}
            {/*
              ⟦rastro · 30/9/2026 · fix/consola-literales-de-brazo⟧ ~~«Afecta a los dos
              brazos»~~ fijo, que `OLI` (un brazo) mostraba igual: sale de `aLosBrazos()`.
            */}
            {/* ⟲ L6 (revisión): con un solo brazo no se lo nombra dos veces, como en `tabla.tsx`. */}
            {brazos().length >= 2
              ? `Afecta ${aLosBrazos()}: el chatbot de ${sellosDeLosBrazos().join(" y de ")} deja de usar esto desde este momento.`
              : `Afecta ${aLosBrazos()}: el chatbot deja de usar esto desde este momento.`}
          </p>
          {/*
            Se dice que no hay vuelta atrás ANTES de confirmar, no después.

            `D11` deja establecido que no hay versionado ni historial: no existe
            de dónde recuperar esto. Una confirmación genérica («¿estás seguro?»)
            no le da al operador la información que necesita para decidir.
          */}
          <p className="carga__aviso">
            Esta acción no se puede deshacer. No hay historial ni papelera: lo eliminado no se
            puede recuperar.
          </p>

          {error !== null && (
            <p className="carga__error" role="alert">
              No se pudo eliminar: {error}
            </p>
          )}

          {/*
            ⭐⭐ ENMIENDA `[21/9/2026]` · **EL ORDEN DEL PIE: «Cancelar» PRIMERO,
            la acción de peligro DESPUÉS.** Antes iban al revés
            (~~`peligro-solido` y después `Cancelar`~~) y **nadie lo había
            mirado**: `r11-orden-de-bloques.test.tsx` declara los popups como su
            punto ciego —«exigen apretar algo antes; este barrido monta y lee»—,
            y lo que había (`r11-acciones-de-fila.test.tsx`, `aw3-acciones`,
            `gates/pantalla.spec.ts`) busca los dos botones **por texto**, que es
            ciego al orden por construcción.

            ⛔ Clasificado como lado **②** de `ADR-0319` —ningún criterio
            `| R1.1-F*-CA-* |` nombra el ORDEN de estos dos botones— y con TRES
            fuentes más además del boceto, las tres diciendo lo mismo:

              · el boceto `BancoEliminar.dc.html:74` ·
                `<span class="btn">Cancelar</span>` y después
                `<span class="btn btn-peligro">Sí, eliminar en los dos brazos</span>`;
              · `PRD_R1.1.md:676` · *«… «Cancelar» y «Sí, eliminar en los dos
                brazos». En todos los popups, Escape equivale a «Cancelar».»*;
              · `R1.1_bocetos/sistema-visual.md:249` §3.11 · *«**Pie:** … botones
                alineados a la derecha: «Cancelar» (secundario) y la acción
                (peligro).»*
              · y el popup que el Anexo B (`PRD_R1.1.md:1275`) adopta como modelo
                para todos —`ConfigGuardrailEliminar.dc.html`, `.dlg-pie`— tiene
                el MISMO orden: «Cancelar» y después «Eliminar categoría».

            ⚠️ Lo que NO se tocó, y por qué: `R1.1-F5-CA-93` (`:1177`) fija el
            FONDO y el RÓTULO de este botón, **no su posición** — es el paso (b)
            de la regla dura de `ADR-0319`: el sujeto del criterio es el color y
            el texto, así que no sirve para justificar el orden en ninguna de las
            dos direcciones. Y `R1.1-F5-CA-106` (`:1190`) fija la ALINEACIÓN a la
            derecha de un popup **sin boceto**: `justify-content: flex-end` de
            `.dialogo__acciones` sigue igual y éste TIENE boceto.

            ⛔ La regla `.dialogo__acciones` de `estilos.css` es `display:flex`
            con `justify-content:flex-end` y **sin `row-reverse`**, así que el
            orden del DOM **es** el orden visual: esto se puede afirmar sin
            cascada, que es lo único que `happy-dom` deja medir. (Se la nombra
            por su SELECTOR y no por su línea a propósito: `estilos.css` se movió
            60 líneas mientras se escribía esto.)
          */}
          <footer className="dialogo__acciones">
            <button
              type="button"
              className="boton"
              onClick={() => setAbierto("ninguno")}
              disabled={enCurso}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="boton boton--peligro-solido"
              onClick={() => {
                void eliminar()
              }}
              disabled={enCurso}
            >
              {/*
                ⭐ ENMIENDA `R1.1-F5-CA-93` (hoy `PRD_R1.1.md:1177`; el número
                `:912` que decía este renglón es de un árbol anterior) ·
                ~~«Sí, eliminar»~~ → **«Sí, eliminar en los dos brazos»**. El
                criterio nombra el rótulo entero, y el alcance es lo que
                distingue a ESTE diálogo de los otros «Eliminar» del repo: el
                documento se va de los dos brazos. El cuerpo ya lo decía
                («Afecta a los dos brazos…»); el botón lo repetía sólo por color.

                ⛔ Medido antes de tocarlo: **ningún `gates/**` mide este diálogo**
                —el que fija «Sí, eliminar» exacto es
                `confirmacion-al-borrar-una-categoria.spec.ts`, que es el de
                CATEGORÍAS y no el del banco—, así que el rótulo largo no apaga
                ningún examen ajeno.
                ⚠️ RASTRO `[21/9/2026]`: eso dejó de ser cierto — hoy
                `gates/pantalla.spec.ts` lo afirma por igualdad exacta. Sigue
                buscándolo **por texto**, así que mover el botón no lo toca.
              */}
              {enCurso ? "Eliminando…" : `Sí, eliminar en ${losBrazos()}`}
            </button>
          </footer>
        </Dialogo>
      )}

      {/* ── `guard` ② · EL MOTIVO, TAL COMO VINO ──────────────────────────────── */}
      {abierto === "motivo" && (
        <Dialogo titulo="Motivo del error" onCerrar={() => setAbierto("ninguno")}>
          <p className="acciones__documento">{documento.name}</p>
          {/*
            ⟲ RASTRO · 25/9/2026 · `AW60` (ADR-0384 A Decisión 2). Decía
            «`documento.errorReason` y NADA más». Hoy: `errorReason` y, debajo,
            los renglones de `renglonesDelMotivo` —etapa y causa traducidas por
            tabla cerrada, detalle del proveedor tal como lo fija el contrato—.
            El resto del criterio sigue igual, textual:

            No se concatena el status de ningún proveedor, ni un stack, ni el
            mensaje de la última llamada fallida. (El estado del proveedor que puede
            traer `errorProviderDetail` lo arma y lo valida el CONTRATO
            —`providerDetailFor`—, no esta pantalla: acá se muestra tal cual.) `K5` ya decidió que este texto
            venga en lenguaje legible; enriquecerlo acá es la salida cómoda que
            `etapas/monitor.md` nombra como modo de falla.

            `null` con estado `error` es una inconsistencia del borde y se dice
            como tal: inventar una causa sería peor, porque el operador la creería.
          */}
          <p className="acciones__motivo">
            {documento.errorReason ??
              "El documento está en error y el sistema no informó un motivo. Avisa a soporte técnico."}
          </p>
          {renglonesDelMotivo(documento).length > 0 && (
            <dl className="acciones__diagnostico">
              {renglonesDelMotivo(documento).map(({ rotulo, valor }) => (
                <div key={rotulo} className="acciones__diagnostico-renglon">
                  <dt>{rotulo}</dt>
                  <dd>{valor}</dd>
                </div>
              ))}
            </dl>
          )}
          <footer className="dialogo__acciones">
            <button type="button" className="boton" onClick={() => setAbierto("ninguno")}>
              Cerrar
            </button>
          </footer>
        </Dialogo>
      )}
    </div>
  )
}
